/**
 * OfflineVoiceCommands - Offline Voice Command Caching System
 * 
 * This module provides:
 * - IndexedDB caching for voice commands
 * - Service Worker registration for offline support
 * - Offline command queue that syncs when online
 * - Local speech recognition without network
 */

// IndexedDB database name and version
const DB_NAME = "goodwin_voice_commands";
const DB_VERSION = 1;
const COMMANDS_STORE = "commands";
const QUEUE_STORE = "offline_queue";
const SETTINGS_STORE = "settings";

// Open IndexedDB database
const openDatabase = () => {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);

    request.onerror = () => reject(request.error);
    request.onsuccess = () => resolve(request.result);

    request.onupgradeneeded = (event) => {
      const db = event.target.result;

      // Commands store
      if (!db.objectStoreNames.contains(COMMANDS_STORE)) {
        const commandsStore = db.createObjectStore(COMMANDS_STORE, { keyPath: "phrase" });
        commandsStore.createIndex("category", "category", { unique: false });
        commandsStore.createIndex("action", "action", { unique: false });
      }

      // Offline queue store
      if (!db.objectStoreNames.contains(QUEUE_STORE)) {
        const queueStore = db.createObjectStore(QUEUE_STORE, { keyPath: "id", autoIncrement: true });
        queueStore.createIndex("timestamp", "timestamp", { unique: false });
        queueStore.createIndex("synced", "synced", { unique: false });
      }

      // Settings store
      if (!db.objectStoreNames.contains(SETTINGS_STORE)) {
        db.createObjectStore(SETTINGS_STORE, { keyPath: "key" });
      }
    };
  });
};

// ============================================
// COMMAND CACHING
// ============================================

/**
 * Cache voice commands locally for offline use
 */
export const cacheCommands = async (commands) => {
  try {
    const db = await openDatabase();
    const transaction = db.transaction(COMMANDS_STORE, "readwrite");
    const store = transaction.objectStore(COMMANDS_STORE);

    // Clear existing commands
    await new Promise((resolve, reject) => {
      const clearRequest = store.clear();
      clearRequest.onsuccess = resolve;
      clearRequest.onerror = reject;
    });

    // Add new commands
    for (const command of commands) {
      store.add({
        phrase: command.phrase.toLowerCase(),
        action: command.action,
        category: command.category,
        is_default: command.is_default,
        cached_at: new Date().toISOString(),
      });
    }

    await new Promise((resolve, reject) => {
      transaction.oncomplete = resolve;
      transaction.onerror = reject;
    });

    // Update last cache timestamp
    await setSetting("commands_cached_at", new Date().toISOString());
    await setSetting("commands_count", commands.length);

    console.log(`[Offline] Cached ${commands.length} voice commands`);
    return true;
  } catch (error) {
    console.error("[Offline] Failed to cache commands:", error);
    return false;
  }
};

/**
 * Get cached commands from IndexedDB
 */
export const getCachedCommands = async () => {
  try {
    const db = await openDatabase();
    const transaction = db.transaction(COMMANDS_STORE, "readonly");
    const store = transaction.objectStore(COMMANDS_STORE);

    return new Promise((resolve, reject) => {
      const request = store.getAll();
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error("[Offline] Failed to get cached commands:", error);
    return [];
  }
};

/**
 * Resolve a phrase to a command (offline-capable)
 */
export const resolveCommandOffline = async (phrase) => {
  const phraseLower = phrase.toLowerCase().trim();
  const commands = await getCachedCommands();

  // Exact match
  const exactMatch = commands.find((c) => c.phrase === phraseLower);
  if (exactMatch) {
    return {
      resolved: true,
      action: exactMatch.action,
      category: exactMatch.category,
      source: "cached_exact",
    };
  }

  // Fuzzy match
  const fuzzyMatch = commands.find(
    (c) => phraseLower.includes(c.phrase) || c.phrase.includes(phraseLower)
  );
  if (fuzzyMatch) {
    return {
      resolved: true,
      action: fuzzyMatch.action,
      category: fuzzyMatch.category,
      source: "cached_fuzzy",
      matched_command: fuzzyMatch.phrase,
    };
  }

  return {
    resolved: false,
    original_phrase: phrase,
  };
};

// ============================================
// OFFLINE QUEUE
// ============================================

/**
 * Add an action to the offline queue for later sync
 */
export const queueOfflineAction = async (action) => {
  try {
    const db = await openDatabase();
    const transaction = db.transaction(QUEUE_STORE, "readwrite");
    const store = transaction.objectStore(QUEUE_STORE);

    const queueItem = {
      action: action.type,
      data: action.data,
      timestamp: new Date().toISOString(),
      synced: false,
      retries: 0,
    };

    await new Promise((resolve, reject) => {
      const request = store.add(queueItem);
      request.onsuccess = resolve;
      request.onerror = reject;
    });

    console.log("[Offline] Action queued for sync:", action.type);
    return true;
  } catch (error) {
    console.error("[Offline] Failed to queue action:", error);
    return false;
  }
};

/**
 * Get all pending items in the offline queue
 */
export const getOfflineQueue = async () => {
  try {
    const db = await openDatabase();
    const transaction = db.transaction(QUEUE_STORE, "readonly");
    const store = transaction.objectStore(QUEUE_STORE);

    return new Promise((resolve, reject) => {
      const request = store.getAll();
      request.onsuccess = () => {
        // Filter for unsynced items in memory since IDBKeyRange.only doesn't work with booleans
        const unsyncedItems = (request.result || []).filter(item => item.synced === false);
        resolve(unsyncedItems);
      };
      request.onerror = () => reject(request.error);
    });
  } catch (error) {
    console.error("[Offline] Failed to get offline queue:", error);
    return [];
  }
};

/**
 * Mark a queue item as synced
 */
export const markQueueItemSynced = async (id) => {
  try {
    const db = await openDatabase();
    const transaction = db.transaction(QUEUE_STORE, "readwrite");
    const store = transaction.objectStore(QUEUE_STORE);

    const item = await new Promise((resolve, reject) => {
      const request = store.get(id);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });

    if (item) {
      item.synced = true;
      item.synced_at = new Date().toISOString();
      await new Promise((resolve, reject) => {
        const request = store.put(item);
        request.onsuccess = resolve;
        request.onerror = reject;
      });
    }

    return true;
  } catch (error) {
    console.error("[Offline] Failed to mark item synced:", error);
    return false;
  }
};

/**
 * Sync offline queue when back online
 */
export const syncOfflineQueue = async (apiUrl) => {
  const queue = await getOfflineQueue();
  
  if (queue.length === 0) {
    console.log("[Offline] No items to sync");
    return { synced: 0, failed: 0 };
  }

  console.log(`[Offline] Syncing ${queue.length} queued items...`);
  
  let synced = 0;
  let failed = 0;

  for (const item of queue) {
    try {
      // Determine endpoint based on action type
      let endpoint = "";
      let method = "POST";
      
      switch (item.action) {
        case "phone_log":
          endpoint = "/api/ehr/phone-logs";
          break;
        case "vital_signs":
          endpoint = "/api/ehr/vitals";
          break;
        case "pain_assessment":
          endpoint = "/api/ehr/assessments/pain";
          break;
        case "visit_note":
          endpoint = "/api/visit-templates/save";
          break;
        default:
          endpoint = `/api/ehr/${item.action}`;
      }

      const response = await fetch(`${apiUrl}${endpoint}`, {
        method,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify(item.data),
      });

      if (response.ok) {
        await markQueueItemSynced(item.id);
        synced++;
      } else {
        failed++;
      }
    } catch (error) {
      console.error(`[Offline] Failed to sync item ${item.id}:`, error);
      failed++;
    }
  }

  console.log(`[Offline] Sync complete: ${synced} synced, ${failed} failed`);
  return { synced, failed };
};

// ============================================
// SETTINGS
// ============================================

/**
 * Set a setting value
 */
export const setSetting = async (key, value) => {
  try {
    const db = await openDatabase();
    const transaction = db.transaction(SETTINGS_STORE, "readwrite");
    const store = transaction.objectStore(SETTINGS_STORE);

    await new Promise((resolve, reject) => {
      const request = store.put({ key, value });
      request.onsuccess = resolve;
      request.onerror = reject;
    });

    return true;
  } catch (error) {
    console.error("[Offline] Failed to set setting:", error);
    return false;
  }
};

/**
 * Get a setting value
 */
export const getSetting = async (key, defaultValue = null) => {
  try {
    const db = await openDatabase();
    const transaction = db.transaction(SETTINGS_STORE, "readonly");
    const store = transaction.objectStore(SETTINGS_STORE);

    const result = await new Promise((resolve, reject) => {
      const request = store.get(key);
      request.onsuccess = () => resolve(request.result);
      request.onerror = () => reject(request.error);
    });

    return result ? result.value : defaultValue;
  } catch (error) {
    console.error("[Offline] Failed to get setting:", error);
    return defaultValue;
  }
};

// ============================================
// OFFLINE STATUS HOOK
// ============================================

/**
 * React hook for managing offline status and sync
 */
export const useOfflineStatus = () => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [queueLength, setQueueLength] = useState(0);
  const [lastSync, setLastSync] = useState(null);

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true);
      // Auto-sync when coming back online
      syncOfflineQueue(process.env.REACT_APP_BACKEND_URL).then((result) => {
        if (result.synced > 0) {
          setLastSync(new Date().toISOString());
        }
      });
    };

    const handleOffline = () => {
      setIsOnline(false);
    };

    window.addEventListener("online", handleOnline);
    window.addEventListener("offline", handleOffline);

    // Check queue length periodically
    const checkQueue = async () => {
      const queue = await getOfflineQueue();
      setQueueLength(queue.length);
    };
    checkQueue();
    const interval = setInterval(checkQueue, 5000);

    return () => {
      window.removeEventListener("online", handleOnline);
      window.removeEventListener("offline", handleOffline);
      clearInterval(interval);
    };
  }, []);

  return { isOnline, queueLength, lastSync };
};

// ============================================
// INITIALIZATION
// ============================================

/**
 * Initialize offline voice commands system
 */
export const initOfflineVoiceCommands = async (apiUrl) => {
  try {
    // Check if commands are already cached
    const cachedAt = await getSetting("commands_cached_at");
    const now = new Date();
    
    // Refresh cache if older than 24 hours or not cached
    const shouldRefresh = !cachedAt || 
      (now - new Date(cachedAt)) > 24 * 60 * 60 * 1000;

    if (navigator.onLine && shouldRefresh) {
      // Fetch fresh commands
      const response = await fetch(`${apiUrl}/api/voice-commands/defaults`);
      if (response.ok) {
        const data = await response.json();
        await cacheCommands(data.commands || []);
      }
    }

    // Register for online/offline events
    window.addEventListener("online", async () => {
      console.log("[Offline] Back online, syncing...");
      await syncOfflineQueue(apiUrl);
    });

    console.log("[Offline] Voice commands system initialized");
    return true;
  } catch (error) {
    console.error("[Offline] Initialization failed:", error);
    return false;
  }
};

// Import useState and useEffect for the hook
import { useState, useEffect } from "react";

export default {
  cacheCommands,
  getCachedCommands,
  resolveCommandOffline,
  queueOfflineAction,
  getOfflineQueue,
  syncOfflineQueue,
  initOfflineVoiceCommands,
  useOfflineStatus,
};
